<script lang="ts" setup>
import type { Product, ProductAliases } from '~/config'

interface Props {
  initialProduct?: ProductAliases | null
}

const props = defineProps<Props>()

const { LocalConfigurationStore } = stores()

const heading = computed(() => LocalConfigurationStore.value?.siteInfo?.productSectionTitle)

const productsInfo = computed(() =>
  LocalConfigurationStore.value?.productsInfo?.filter((p) => p.enabled)
)

const currentProduct = ref<Product>()
const { visible: isDialogVisible, show: showDialog, hide: hideDialog } = useVisible()
const displayProduct = (product: Product) => {
  currentProduct.value = product
  showDialog()
}

const productImgIds = computed(
  () => productsInfo.value?.flatMap((p) => [`${p.code}-artwork`, `${p.code}-artwork-icon`]) ?? []
)

const { allCompleted } = useImageLoaderTracker(productImgIds)

const isReady = computed(() => allCompleted.value)

onMounted(() => {
  const initial = productsInfo.value?.find((product) => product.code === props.initialProduct)
  if (initial) displayProduct(initial)
})
</script>

<template>
  <div class="flex flex-col gap-1">
    <TitleComponent
      v-if="heading"
      :text="heading.title"
      :tagline="heading?.subTitle"
    />

    <GridComponent
      :cols="4"
      :gap="$viewport.isGreaterThan('sm') ? 'sm' : 'xs'"
    >
      <template
        v-for="product in productsInfo?.sort(
          (a, b) => (a?.priority ?? 1000) - (b?.priority ?? 1000)
        )"
        :key="product.code"
      >
        <div>
          <PvSkeleton
            v-if="!isReady"
            class="aspect-[16/9] w-full"
          />
          <ClientOnly>
            <ContentProductThumbnail
              v-show="isReady"
              :product="product"
              @xclick="displayProduct(product)"
              @click="navigateTo(`/product/${product.code}`, { external: true })"
            />
          </ClientOnly>
        </div>
      </template>
    </GridComponent>

    <ContentProductDialog
      v-if="currentProduct"
      :visible="isDialogVisible"
      :product-code="currentProduct.code"
      @close="hideDialog"
    />
  </div>
</template>
